import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useConfirm } from '../../components/shared/Confirm';
import PlatformNotifications from './PlatformNotificationsPage';
import { useIsMobile } from '../../utils/useIsMobile';

const ESTADOS = {
  open:          { texto: 'Conectado',                    color: '#16a34a' },
  connecting:    { texto: 'Esperando escaneo',            color: '#d97706' },
  close:         { texto: 'Desconectado',                 color: '#ef4444' },
  sin_instancia: { texto: 'Sin vincular',                 color: 'var(--color-text-muted)' },
  no_disponible: { texto: 'Evolution API sin configurar', color: '#ef4444' },
  desconocido:   { texto: 'Estado desconocido',           color: 'var(--color-text-muted)' },
};

// Qué sale desde este número. Se lista explícito para que quede claro el
// alcance antes de vincular.
const AVISOS = [
  {
    titulo: 'Gasto fijo por vencer',
    quien: 'Super administradores',
    detalle: 'Cinco días antes del vencimiento de cada gasto fijo de la plataforma.',
    campo: 'superadmins',
  },
  {
    titulo: 'Suscripción por vencer, vencida o suspendida',
    quien: 'Administrador de cada negocio',
    detalle: 'Al acercarse el vencimiento, al vencer y al suspenderse el servicio.',
    campo: 'administradores',
  },
];

const PESTANAS = [
  { key: 'whatsapp',      label: '📲 WhatsApp' },
  { key: 'notificaciones', label: '🔔 Notificaciones' },
];

export default function IntegrationsPage() {
  const [tab, setTab] = useState('whatsapp');

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Integraciones</h1>
          <p className="page-subtitle">Servicios externos conectados a la plataforma</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: '1.5rem', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
        {PESTANAS.map(p => {
          const sel = tab === p.key;
          return (
            <button key={p.key} onClick={() => setTab(p.key)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '0.6rem 1rem',
              fontSize: '0.9rem', fontFamily: 'var(--font-body)', fontWeight: sel ? 700 : 500,
              color: sel ? 'var(--color-gold)' : 'var(--color-text-muted)',
              borderBottom: `2px solid ${sel ? 'var(--color-gold)' : 'transparent'}`,
              marginBottom: -1,
            }}>{p.label}</button>
          );
        })}
      </div>

      {tab === 'whatsapp' ? <WhatsAppPlataforma /> : <PlatformNotifications />}
    </div>
  );
}

function WhatsAppPlataforma() {
  const esMovil = useIsMobile(900);

  const [estado, setEstado]     = useState(null);
  const [qr, setQr]             = useState(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado]   = useState(false);
  const [aviso, setAviso]       = useState('');
  const [error, setError]       = useState('');
  const confirmar = useConfirm();

  const consultar = useCallback(async () => {
    try {
      const r = await api.get('/whatsapp/plataforma/status');
      setEstado(r.data);
      if (r.data.state === 'open') setQr(null);   // conectado: el QR ya no sirve
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo consultar el estado');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { consultar(); }, [consultar]);

  // Mientras hay un QR en pantalla se comprueba solo si ya lo escanearon.
  useEffect(() => {
    if (!qr) return undefined;
    const t = setInterval(consultar, 5000);
    return () => clearInterval(t);
  }, [qr, consultar]);

  const vincular = async () => {
    setOcupado(true); setError(''); setAviso('');
    try {
      await api.post('/whatsapp/plataforma/instance');
      const r = await api.get('/whatsapp/plataforma/qr');
      if (r.data.qr) {
        setQr(r.data.qr);
        setAviso('Escanea el código desde WhatsApp → Dispositivos vinculados → Vincular dispositivo.');
      } else {
        setAviso('No llegó ningún código. Puede que el número ya esté vinculado.');
        consultar();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo generar el código');
    } finally { setOcupado(false); }
  };

  const desvincular = async () => {
    if (!await confirmar({
      titulo: 'Desvincular WhatsApp de la plataforma',
      mensaje: 'Se cerrará la sesión de WhatsApp de la plataforma.',
      detalle: 'Dejarán de salir los avisos de gastos por vencer y de suscripciones hasta vincular otro número.',
      aceptar: 'Desvincular',
      peligro: true,
    })) return;
    setOcupado(true); setError(''); setAviso('');
    try {
      const r = await api.delete('/whatsapp/plataforma/session');
      setAviso(r.data.message);
      setQr(null);
      consultar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo desvincular');
    } finally { setOcupado(false); }
  };

  const info = ESTADOS[estado?.state] || ESTADOS.desconocido;
  const conectado = estado?.state === 'open';

  return (
    <>
      {aviso && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{aviso}</div>}
      {error && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? '1fr' : 'minmax(0,1fr) 340px',
        gap: '1.25rem', alignItems: 'start',
      }}>
        {/* Estado y vinculación */}
        <div className="card" style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>📲 WhatsApp de la plataforma</h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                El número desde el que salen los avisos internos.
              </p>
            </div>
            <span style={{ fontSize: '0.84rem', color: info.color, fontWeight: 700, whiteSpace: 'nowrap' }}>
              ● {info.texto}
            </span>
          </div>

          {cargando ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
          ) : (
            <>
              <div style={{ margin: '1rem 0', fontSize: '0.85rem' }}>
                {conectado && estado?.whatsapp_numero && (
                  <div style={{ marginBottom: 6 }}>
                    Número vinculado: <strong>{estado.whatsapp_numero}</strong>
                  </div>
                )}
                {estado?.desde_entorno && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    Usando la instancia de la variable de entorno. Al vincular aquí, esta pantalla manda.
                  </div>
                )}
              </div>

              {qr && (
                <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                  <img src={qr} alt="Código QR para vincular WhatsApp"
                       style={{ width: 240, height: 240, borderRadius: 10, background: '#fff', padding: 8 }} />
                  <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>
                    El código caduca en menos de un minuto. Si expira, vuelve a generarlo.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={vincular} disabled={ocupado}>
                  {ocupado ? 'Generando...' : conectado ? 'Vincular otro número' : '📱 Vincular WhatsApp'}
                </button>
                <button className="btn btn-ghost" onClick={consultar} disabled={ocupado}>Ver estado</button>
                {conectado && (
                  <button className="btn btn-ghost" onClick={desvincular} disabled={ocupado}>Desvincular</button>
                )}
              </div>

              {!conectado && (
                <p style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                  Mientras no haya un número vinculado, los avisos siguen apareciendo dentro
                  del panel pero no se envían por WhatsApp. Tampoco se marcan como enviados,
                  así que saldrán en cuanto vincules.
                </p>
              )}
            </>
          )}
        </div>

        {/* Qué sale desde aquí */}
        <div className="card" style={{ alignSelf: 'start' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: '0.95rem' }}>Avisos que salen de aquí</h3>
          <p style={{ margin: '0 0 12px', fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
            Cada persona los recibe en el celular guardado en su usuario.
          </p>

          {AVISOS.map(a => {
            const n = estado?.[a.campo] ?? 0;
            return (
              <div key={a.titulo} style={{ paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid var(--color-border)' }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{a.titulo}</div>
                <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                  {a.detalle}
                </div>
                <div style={{ fontSize: '0.76rem', marginTop: 4, color: n ? '#16a34a' : '#d97706' }}>
                  {a.quien}: <strong>{n}</strong> con celular cargado
                  {!n && ' — nadie lo recibiría'}
                </div>
              </div>
            );
          })}

          <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
            Para recibir no hace falta vincular nada: basta con tener el celular en el
            usuario, con el 57 delante. Se edita en Usuarios.
          </p>
        </div>
      </div>
    </>
  );
}
